// src/services/productCategoriesService.js
//
// Frontend API wrapper for Product Categories in the Catalog domain.
//
// PURPOSE
// -------
// This service handles top-level product grouping used by employees
// to maintain the salon product base in hb_visits.
//
// Product categories are intentionally simpler than service hierarchy.
// They still need clean CRUD, activation control and later website publication support.
//
// Logging policy and error style are aligned with visitService.js.
//

import apiClient from "../utils/apiClient";

const DEBUG_API =
  String(process.env.REACT_APP_DEBUG_API || "").toLowerCase().trim() === "true";

function getErrorMessage(err) {
  const apiMsg =
    err?.response?.data?.message ||
    err?.response?.data?.error ||
    err?.response?.data?.detail;

  return apiMsg || err?.message || "Request failed";
}

function throwApiError(context, err) {
  const message = getErrorMessage(err);
  const e = new Error(`${context}: ${message}`);

  e.status = err?.response?.status;
  e.data = err?.response?.data;

  if (DEBUG_API) {
    // eslint-disable-next-line no-console
    console.error(`[ProductCategoriesService] ${context}`, {
      status: e.status,
      message,
      data: e.data,
    });
  }

  throw e;
}

/**
 * Fetch product categories.
 *
 * Backend:
 *   GET /catalog/product-categories
 *
 * Optional query params:
 *   - active=true|false
 *
 * Return:
 * - expected: array of product category objects
 * - defensive fallback: [] if backend payload is missing / malformed
 */
export const fetchProductCategories = async (params = {}) => {
  try {
    const res = await apiClient.get("/catalog/product-categories", {
      withCredentials: true,
      params,
    });

    const payload = res?.data;
    return Array.isArray(payload) ? payload : [];
  } catch (err) {
    throwApiError("fetchProductCategories", err);
  }
};

/**
 * Create a new product category.
 *
 * Backend:
 *   POST /catalog/product-categories
 *
 * Expected body:
 *   {
 *     name_internal: string,
 *     name_public: string,
 *     slug?: string,
 *     description_short?: string,
 *     sort_order?: number,
 *     is_active?: boolean,
 *     publish_to_website?: boolean
 *   }
 */
export const createProductCategory = async (payload) => {
  try {
    if (!payload || typeof payload !== "object") {
      const e = new Error("createProductCategory: payload object is required.");
      e.code = "PC_PAYLOAD_MISSING";
      throw e;
    }

    if (!payload.name_internal || `${payload.name_internal}`.trim() === "") {
      const e = new Error("createProductCategory: name_internal is required.");
      e.code = "PC_NAME_INTERNAL_MISSING";
      throw e;
    }

    if (!payload.name_public || `${payload.name_public}`.trim() === "") {
      const e = new Error("createProductCategory: name_public is required.");
      e.code = "PC_NAME_PUBLIC_MISSING";
      throw e;
    }

    const res = await apiClient.post("/catalog/product-categories", payload, {
      withCredentials: true,
      headers: { "Content-Type": "application/json" },
    });

    return res?.data;
  } catch (err) {
    throwApiError("createProductCategory", err);
  }
};

/**
 * Update an existing product category.
 *
 * Backend:
 *   PUT /catalog/product-categories/:id
 */
export const updateProductCategory = async (id, payload) => {
  try {
    if (id === undefined || id === null || `${id}`.trim() === "") {
      const e = new Error("updateProductCategory: id is required.");
      e.code = "PC_ID_MISSING";
      throw e;
    }

    if (!payload || typeof payload !== "object") {
      const e = new Error("updateProductCategory: payload object is required.");
      e.code = "PC_PAYLOAD_MISSING";
      throw e;
    }

    const res = await apiClient.put(`/catalog/product-categories/${id}`, payload, {
      withCredentials: true,
      headers: { "Content-Type": "application/json" },
    });

    return res?.data;
  } catch (err) {
    throwApiError("updateProductCategory", err);
  }
};

/**
 * Toggle active state of a product category.
 *
 * Backend:
 *   PATCH /catalog/product-categories/:id/active
 *   Body: { active: true|false }
 */
export const toggleProductCategoryActive = async (id, active) => {
  try {
    if (id === undefined || id === null || `${id}`.trim() === "") {
      const e = new Error("toggleProductCategoryActive: id is required.");
      e.code = "PC_ID_MISSING";
      throw e;
    }

    const res = await apiClient.patch(
      `/catalog/product-categories/${id}/active`,
      { active: !!active },
      {
        withCredentials: true,
        headers: { "Content-Type": "application/json" },
      }
    );

    return res?.data;
  } catch (err) {
    throwApiError("toggleProductCategoryActive", err);
  }
};

/**
 * Delete a product category.
 *
 * Backend:
 *   DELETE /catalog/product-categories/:id
 *
 * Backend may reject delete when:
 * - the category has publication history
 * - the category still contains child products
 */
export const deleteProductCategory = async (id) => {
  try {
    if (id === undefined || id === null || `${id}`.trim() === "") {
      const e = new Error("deleteProductCategory: id is required.");
      e.code = "PC_ID_MISSING";
      throw e;
    }

    const res = await apiClient.delete(`/catalog/product-categories/${id}`, {
      withCredentials: true,
    });

    return res?.data ?? { ok: true };
  } catch (err) {
    throwApiError("deleteProductCategory", err);
  }
};

export default {
  fetchProductCategories,
  createProductCategory,
  updateProductCategory,
  toggleProductCategoryActive,
  deleteProductCategory,
};